// S5 (v0.16.0) — Bulk camera import wizard.
// Three steps: prepare CSV → upload → results. Validation errors come
// back from the backend as a structured list (row + column + error)
// and we render them in a sortable table.

import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  downloadImportTemplate,
  useImportCameras,
  type ImportErrorRow,
  type ImportResult,
} from "@/api/s5";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

type Step = "prepare" | "upload" | "results";

const COLUMN_DOCS: Array<{ col: string; req: boolean; note: string }> = [
  { col: "name", req: true, note: "Camera display name" },
  { col: "rtsp_url", req: true, note: "Full RTSP URL starting with rtsp://" },
  { col: "entity_code", req: false, note: "Site code (e.g. BR-001)" },
  { col: "protocol", req: false, note: "rtsp / onvif / isapi (default rtsp)" },
  { col: "vendor", req: false, note: "Camera manufacturer" },
  { col: "model", req: false, note: "Camera model" },
  { col: "firmware_version", req: false, note: "Firmware version" },
  { col: "serial_number", req: false, note: "Serial number" },
  { col: "username", req: false, note: "Camera login username" },
  { col: "password", req: false, note: "Camera login password" },
  { col: "recording_enabled", req: false, note: "true / false (default true)" },
  { col: "retention_days", req: false, note: "1..365 (default 30)" },
  { col: "profile_name", req: false, note: "Existing device profile name" },
  { col: "tags", req: false, note: "Comma-separated, e.g. lobby,ptz" },
  { col: "notes", req: false, note: "Free text" },
];

export default function BulkImportPage() {
  const [step, setStep] = useState<Step>("prepare");
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [errors, setErrors] = useState<ImportErrorRow[] | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const importer = useImportCameras();
  const nav = useNavigate();

  async function handleDownload() {
    const blob = await downloadImportTemplate();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "camera_import_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleImport() {
    if (!file) return;
    setErrors(null);
    setServerError(null);
    try {
      const r = await importer.mutateAsync(file);
      setResult(r);
      setStep("results");
    } catch (err) {
      const detail = (
        err as { response?: { status?: number; data?: { detail?: unknown } } }
      ).response?.data?.detail;
      if (
        typeof detail === "object" &&
        detail !== null &&
        "errors" in (detail as Record<string, unknown>)
      ) {
        const rows = (detail as { errors: ImportErrorRow[] }).errors;
        setErrors(rows.sort((a, b) => a.row - b.row));
      } else {
        setServerError(
          typeof detail === "string" ? detail : "Import failed unexpectedly",
        );
      }
      setStep("results");
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Bulk Import Cameras</h1>
        <p className="text-sm text-muted-foreground">
          All-or-nothing. Every row is validated up front; if any row
          fails, nothing is imported.
        </p>
      </div>

      <Stepper step={step} />

      {step === "prepare" && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="text-lg font-semibold">Step 1 — Download template</h2>
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="py-1">Column</th>
                  <th className="py-1">Required</th>
                  <th className="py-1">Description</th>
                </tr>
              </thead>
              <tbody>
                {COLUMN_DOCS.map((row) => (
                  <tr key={row.col} className="border-t">
                    <td className="py-1 font-mono">{row.col}</td>
                    <td className="py-1">{row.req ? "Yes" : "—"}</td>
                    <td className="py-1 text-muted-foreground">{row.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-between">
              <Button variant="outline" onClick={handleDownload}>
                Download Template
              </Button>
              <Button onClick={() => setStep("upload")}>Next</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === "upload" && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="text-lg font-semibold">Step 2 — Upload CSV</h2>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="text-sm"
            />
            {file && (
              <p className="text-sm text-muted-foreground">
                Selected: <span className="font-medium">{file.name}</span>{" "}
                ({Math.ceil(file.size / 1024)} KB)
              </p>
            )}
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep("prepare")}>
                Back
              </Button>
              <Button
                onClick={handleImport}
                disabled={!file || importer.isPending}
              >
                {importer.isPending ? "Uploading…" : "Import"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === "results" && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="text-lg font-semibold">Step 3 — Results</h2>
            {result && (
              <div className="rounded border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-700">
                Successfully imported {result.imported_count} cameras.
              </div>
            )}
            {errors && (
              <div className="space-y-2">
                <p className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600">
                  Import rejected — {errors.length} row(s) failed validation.
                  Nothing was imported.
                </p>
                <table className="w-full text-xs">
                  <thead className="bg-muted/40 text-left text-muted-foreground">
                    <tr>
                      <th className="px-2 py-1">Row</th>
                      <th className="px-2 py-1">Column</th>
                      <th className="px-2 py-1">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errors.map((e, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-2 py-1 font-mono">{e.row}</td>
                        <td className="px-2 py-1 font-mono">{e.column}</td>
                        <td className="px-2 py-1">{e.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {serverError && (
              <p className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600">
                {serverError}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setStep("prepare");
                  setFile(null);
                  setResult(null);
                  setErrors(null);
                  setServerError(null);
                }}
              >
                Import More
              </Button>
              {result && (
                <Button onClick={() => nav("/cameras")}>View Cameras</Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stepper({ step }: { step: Step }) {
  const steps: Array<{ id: Step; label: string }> = [
    { id: "prepare", label: "Prepare" },
    { id: "upload", label: "Upload" },
    { id: "results", label: "Results" },
  ];
  return (
    <ol className="flex items-center gap-2 text-xs text-muted-foreground">
      {steps.map((s, i) => {
        const active = s.id === step;
        return (
          <li
            key={s.id}
            className={
              active
                ? "rounded bg-primary px-2 py-1 text-primary-foreground"
                : "rounded bg-muted px-2 py-1"
            }
          >
            {i + 1}. {s.label}
          </li>
        );
      })}
    </ol>
  );
}
